/**
 * @fileoverview InventarioMaterialesRepository - Repository for inventario_materiales collection
 */

import { DataRepository, RepositoryError } from './base/DataRepository';
import { LogInventarioMaterialesRepository } from './LogInventarioMaterialesRepository';
import type { 
  InventarioMateriales, 
  InventarioMaterialesExpanded,
  CreateInventarioMaterialesData, 
  UpdateInventarioMaterialesData 
} from '../types/collections/InventarioMateriales';
import type { 
  LogInventarioMateriales,
  LogInventarioMaterialesExpanded
} from '../types/collections/LogInventarioMateriales';
import type { QueryOptions, PaginationResult, ListResult } from '../types/common/PocketBaseTypes';

/**
 * Repository for managing InventarioMateriales records
 * Extends DataRepository with InventarioMateriales-specific methods
 */
export class InventarioMaterialesRepository extends DataRepository<InventarioMateriales> {
  private _logRepository: LogInventarioMaterialesRepository | null = null;

  constructor() {
    super('inventario_materiales');
  }

  /**
   * Lazy getter for log repository to avoid circular dependency issues
   */
  private get logRepository(): LogInventarioMaterialesRepository {
    if (!this._logRepository) {
      this._logRepository = new LogInventarioMaterialesRepository();
    }
    return this._logRepository;
  }

  /**
   * Get active materials with pagination
   * @param page Page number (1-based)
   * @param pageSize Number of records per page
   * @param options Additional query options
   * @returns Paginated result of active materials with unidad expanded
   */
  async getActiveMaterials(
    page: number = 1, 
    pageSize: number = 10, 
    options: QueryOptions = {}
  ): Promise<PaginationResult<InventarioMateriales>> {
    const queryOptions: QueryOptions = {
      filter: 'activo = true',
      sort: 'nombre',
      expand: 'unidad',
      ...options,
    };

    return this.getList(page, pageSize, queryOptions);
  }

  /**
   * Add quantity to material and create log entry
   * @param id Material ID
   * @param cantidad Amount to add
   * @param usuarioId User ID performing the operation
   * @returns Updated material record
   */
  async agregarCantidad(
    id: string, 
    cantidad: number, 
    usuarioId: string
  ): Promise<InventarioMateriales> {
    // Get current material
    const material = await this.getById(id);
    
    // Calculate new total
    const currentTotal = material.total ?? 0;
    const newTotal = currentTotal + cantidad;

    // Create log entry
    const logEntry = await this.logRepository.create({
      usuario: usuarioId,
      cantidad: cantidad,
      ingreso: true,
      activo: true,
    });

    // Update material with new total and add log to movimientos array
    const existingLogs = material.log_movimientos ?? [];
    const updatedLogs = [...existingLogs, logEntry.id];

    return this.update(id, {
      total: newTotal,
      log_movimientos: updatedLogs,
    });
  }

  /**
   * Subtract quantity from material and create log entry
   * @param id Material ID
   * @param cantidad Amount to subtract
   * @param usuarioId User ID performing the operation
   * @returns Updated material record
   */
  async restarCantidad(
    id: string, 
    cantidad: number, 
    usuarioId: string
  ): Promise<InventarioMateriales> {
    // Get current material
    const material = await this.getById(id);
    
    // Calculate new total
    const currentTotal = material.total ?? 0;
    const newTotal = currentTotal - cantidad;

    // Create log entry
    const logEntry = await this.logRepository.create({
      usuario: usuarioId,
      cantidad: cantidad,
      ingreso: false,
      activo: true,
    });

    // Update material with new total and add log to movimientos array
    const existingLogs = material.log_movimientos ?? [];
    const updatedLogs = [...existingLogs, logEntry.id];

    return this.update(id, {
      total: newTotal,
      log_movimientos: updatedLogs,
    });
  }

  /**
   * Get movements for a specific material with pagination
   * Orders movements by date descending (most recent first)
   * @param materialId Material ID
   * @param page Page number (1-based)
   * @param pageSize Number of records per page
   * @returns Paginated result of movements with usuario expanded
   */
  async getMovimientosPorMaterial(
    materialId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<ListResult<LogInventarioMaterialesExpanded>> {
    // Get material with logs expanded
    const material = await this.getById(materialId, {
      expand: 'log_movimientos.usuario',
    });

    // Get logs from expand (if available)
    const logsArray = (material as any).expand?.log_movimientos || [];
    
    // If no logs, return empty result
    if (!logsArray || logsArray.length === 0) {
      return {
        page,
        perPage: pageSize,
        totalItems: 0,
        totalPages: 0,
        items: [],
      };
    }

    // Sort logs by created date descending (most recent first)
    const sortedLogs = [...logsArray].sort((a: any, b: any) => {
      const dateA = new Date(a.created).getTime();
      const dateB = new Date(b.created).getTime();
      return dateB - dateA;
    });

    // Apply pagination manually
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedLogs = sortedLogs.slice(startIndex, endIndex);

    // Calculate pagination metadata
    const totalItems = sortedLogs.length;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      page,
      perPage: pageSize,
      totalItems,
      totalPages,
      items: paginatedLogs as LogInventarioMaterialesExpanded[],
    };
  }

  /**
   * Create new material with validation
   * If total > 0, creates an initial ingreso log entry
   * @param data Material data to create
   * @returns Created material record
   */
  async createMaterial(data: CreateInventarioMaterialesData): Promise<InventarioMateriales> {
    // Validate required fields
    if (!data.nombre || data.nombre.trim() === '') {
      throw new Error('Nombre is required');
    }

    const total = data.total ?? 0;

    // Set default values
    const createData: CreateInventarioMaterialesData = {
      activo: true, // Default to active
      total: total,
      log_movimientos: [], // Initialize empty array
      ...data,
    };

    // Create the material first
    const createdMaterial = await this.create(createData);

    // If total > 0, create an initial ingreso log entry
    if (total > 0) {
      try {
        const usuarioId = this.pb.authStore.model?.id;
        
        if (usuarioId) {
          // Create log entry for initial quantity
          const logEntry = await this.logRepository.create({
            usuario: usuarioId,
            cantidad: total,
            ingreso: true,
            activo: true,
          });

          // Update material with log entry
          return this.update(createdMaterial.id, {
            log_movimientos: [logEntry.id],
          });
        } else {
          // If no user, log warning but don't fail
          console.warn('No user authenticated, skipping initial log entry for material:', createdMaterial.id);
        }
      } catch (error) {
        // Log error but don't fail material creation
        console.error('Error creating initial log entry:', error);
      }
    }

    return createdMaterial;
  }

  /**
   * Soft delete material (set activo = false)
   * @param id Material ID to deactivate
   * @returns Updated material record
   */
  async deactivate(id: string): Promise<InventarioMateriales> {
    return this.update(id, { activo: false });
  }

  /**
   * Restore material (set activo = true)
   * @param id Material ID to activate
   * @returns Updated material record
   */
  async activate(id: string): Promise<InventarioMateriales> {
    return this.update(id, { activo: true });
  }

  /**
   * Get all active materials with expanded relations for Excel export
   * @returns Array of active materials with unidad, log_movimientos, and log_movimientos.usuario expanded
   */
  async getMaterialesParaExportacion(): Promise<InventarioMaterialesExpanded[]> {
    try {
      const materiales = await this.pb.collection(this.collectionName).getFullList<InventarioMaterialesExpanded>({
        filter: 'activo = true',
        expand: 'unidad,log_movimientos,log_movimientos.usuario',
        sort: 'nombre',
      });

      return materiales;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener materiales para exportación: ${error instanceof Error ? error.message : String(error)}`,
        'GET_MATERIALES_EXPORT_ERROR'
      );
    }
  }
}

